import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import { GRNViewData } from 'src/models/Management.interface';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import { formatDate, formatCurrency } from 'src/utils/pdf.utils';
import Logo from 'src/assets/images/Logo.png';
import { GRNStatus } from 'src/models/enums/GRNStatus.enum';

const colors = {
  primary: '#1a3c5e',
  primaryLight: '#3a7bd5',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
  orange: '#e67e22',
  blue: '#3a7bd5',
  success: '#27ae60',
  red: '#c0392b',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 100,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
  },

  // Title
  grnTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    textAlign: 'center',
    marginBottom: 12,
  },
  grnTitleDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: 24,
  },

  // Header row
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '50%',
  },
  companyLogo: {
    width: 50,
    height: 50,
    marginBottom: 8,
  },
  companyTextBlock: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 2,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'normal',
    marginBottom: 2,
  },
  headerRight: {
    width: '45%',
  },
  grnDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  grnDetailLabel: {
    fontSize: 9,
    color: colors.textLight,
  },
  grnDetailValue: {
    fontSize: 9,
    fontWeight: 'normal',
    color: colors.textDark,
  },
  grnInvoiceValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  grnInvoiceAmountValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.textDark,
  },

  // Section label
  sectionLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: 8,
    letterSpacing: 0.5,
  },

  // Table
  table: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundGray,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    backgroundColor: '#fcfcfc',
  },

  // Column widths for GRN table
  colNum: { width: '8%' },
  colItem: { width: '32%' },
  colReceivedQty: { width: '20%', textAlign: 'center' },
  colReturnedQty: { width: '20%', textAlign: 'center' },
  colUnitPrice: { width: '20%', textAlign: 'right' },

  cellText: {
    fontSize: 9,
    color: colors.textDark,
  },
  cellTextLight: {
    fontSize: 9,
    color: colors.textMedium,
  },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F7FF',
    paddingVertical: 12,
    paddingHorizontal: 40,
  },
  footerText: {
    fontSize: 10,
    color: colors.primary,
    fontWeight: 'bold',
    marginRight: 8,
    letterSpacing: 1,
  },
  footerLogo: {
    width: 60,
    height: 20,
    marginBottom: 2,
  },
});

const getStatusLabel = (status: number): string => {
  switch (status) {
    case GRNStatus.NEW:
      return 'New';
    case GRNStatus.PENDING:
      return 'Pending';
    case GRNStatus.APPROVED:
      return 'Approved';
    case GRNStatus.REJECTED:
      return 'Rejected';
    case GRNStatus.PAYMENT_PROGRESS:
      return 'Payment In Progress';
    case GRNStatus.PAID:
      return 'Paid';
    default:
      return 'Unknown';
  }
};

interface GRNPDFProps {
  grn: GRNViewData;
  companyInfo?: CompanyInfoData;
}

export const GRNDocument: React.FC<GRNPDFProps> = ({ grn, companyInfo }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>POWERED BY</Text>
          <Image style={styles.footerLogo} src={Logo} />
        </View>

        {/* ===== GRN TITLE ===== */}
        <Text style={styles.grnTitle}>GRN</Text>
        <View style={styles.grnTitleDivider} />

        {/* ===== HEADER ROW ===== */}
        <View style={styles.headerRow}>
          {/* Company Info - Left Side */}
          <View style={styles.headerLeft}>
            {companyInfo?.logoUrl && (
              <Image style={styles.companyLogo} src={companyInfo.logoUrl} />
            )}
            <View style={styles.companyTextBlock}>
              <Text style={styles.companyName}>
                {companyInfo?.companyName || 'OrderX'}
              </Text>
              {companyInfo && (
                <>
                  <Text style={styles.companyDetails}>
                    {companyInfo.addressLine1}
                    {companyInfo.addressLine2
                      ? `, ${companyInfo.addressLine2}`
                      : ''}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.city}, {companyInfo.postCode}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.country}
                  </Text>
                  {companyInfo.telephoneNumber && (
                    <Text style={styles.companyDetails}>
                      Tel: {companyInfo.telephoneNumber}
                    </Text>
                  )}
                  {companyInfo.email && (
                    <Text style={styles.companyDetails}>
                      Email: {companyInfo.email}
                    </Text>
                  )}
                  {companyInfo.websiteUrl && (
                    <Text style={styles.companyDetails}>
                      Web: {companyInfo.websiteUrl}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>

          {/* GRN Details - Right Side */}
          <View style={styles.headerRight}>
            <View style={styles.grnDetailRow}>
              <Text style={styles.grnDetailLabel}>Invoice Number</Text>
              <Text style={styles.grnInvoiceValue}>#{grn.invoiceNumber}</Text>
            </View>
            <View style={styles.grnDetailRow}>
              <Text style={styles.grnDetailLabel}>Date</Text>
              <Text style={styles.grnDetailValue}>
                {formatDate(grn.createdAt)}
              </Text>
            </View>
            <View style={styles.grnDetailRow}>
              <Text style={styles.grnDetailLabel}>Vendor</Text>
              <Text style={styles.grnDetailValue}>{grn.vendorName}</Text>
            </View>
            <View style={styles.grnDetailRow}>
              <Text style={styles.grnDetailLabel}>Approved By</Text>
              <Text style={styles.grnDetailValue}>{grn.approvedBy || '-'}</Text>
            </View>
            <View style={styles.grnDetailRow}>
              <Text style={styles.grnDetailLabel}>Status</Text>
              <Text style={styles.grnDetailValue}>
                {getStatusLabel(grn.status)}
              </Text>
            </View>
            <View style={styles.grnDetailRow}>
              <Text style={styles.grnDetailLabel}>Amount</Text>
              <Text style={styles.grnInvoiceAmountValue}>
                {formatCurrency(grn.invoiceAmount)}
              </Text>
            </View>
          </View>
        </View>

        {/* ===== ITEMS TABLE ===== */}
        <Text style={styles.sectionLabel}>DETAILS</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.colNum]}>#</Text>
            <Text style={[styles.tableHeaderText, styles.colItem]}>Items</Text>
            <Text style={[styles.tableHeaderText, styles.colReceivedQty]}>
              Received Qty
            </Text>
            <Text style={[styles.tableHeaderText, styles.colReturnedQty]}>
              Returned Qty
            </Text>
            <Text style={[styles.tableHeaderText, styles.colUnitPrice]}>
              Unit Price
            </Text>
          </View>

          {/* Table Rows */}
          {grn.items.map((item, index) => (
            <View
              key={item.id}
              style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
            >
              <Text style={[styles.cellText, styles.colNum]}>
                {String(index + 1).padStart(2, '0')}
              </Text>
              <Text style={[styles.cellText, styles.colItem]}>
                {item.rawMaterialName}
              </Text>
              <Text style={[styles.cellTextLight, styles.colReceivedQty]}>
                {item.receivedQty.toLocaleString('en-US')}
              </Text>
              <Text style={[styles.cellTextLight, styles.colReturnedQty]}>
                {item.returnedQty.toLocaleString('en-US')}
              </Text>
              <Text style={[styles.cellText, styles.colUnitPrice]}>
                {formatCurrency(item.unitPrice)}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export const generateGRNPDF = async (grn: GRNViewData): Promise<void> => {
  try {
    const companyInfo = await getCompanyInfoForPDF();
    const blob = await pdf(
      <GRNDocument grn={grn} companyInfo={companyInfo} />
    ).toBlob();
    const fileName = `GRN-${grn.invoiceNumber}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating GRN PDF:', error);
    throw error;
  }
};

export default GRNDocument;
